import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  hasAdminView,
  useAdminViewRestoring,
  useAdminViewState,
} from "../../lib/admin/adminViewState";
import { apiFetch } from "../../api/client";
import { formatDate, formatNumber, formatRelative } from "../../lib/admin/adminFormat";
import { SortableTh, useTableSort } from "./AdminSortableTable";
import { AdminBoardCard } from "./AdminBoardCard";
import { AdminCopyId } from "./AdminCopyId";

export type AdminBoardRow = {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  in_trash: boolean;
  owner_user_id: string | null;
  owner_email: string | null;
  is_guest: boolean;
  objects: number;
  revision: number;
};

const PAGE_SIZE = 100;
type SortKey = "title" | "owner" | "objects" | "updated" | "created";

/** Модульная функция — ссылка стабильна, useMemo в useTableSort не пересчитывается зря. */
function pickBoardValue(row: AdminBoardRow, key: SortKey): string | number | null {
  if (key === "title") return row.title || "";
  if (key === "owner") return row.owner_email ?? (row.is_guest ? "гостевая" : "");
  if (key === "objects") return row.objects;
  if (key === "updated") return new Date(row.updated_at).getTime();
  return new Date(row.created_at).getTime();
}

export function AdminBoardsTab({ kind }: { kind: "school" | "external" }) {
  const key = (name: string) => `admin:${kind}:boards:${name}`;
  const restoring = useAdminViewRestoring();
  /** Вернулись «Назад» с уже загруженным списком — повторно не грузим. */
  const restoredRef = useRef(restoring && hasAdminView(key("rows")));
  const [rows, setRows] = useAdminViewState<AdminBoardRow[]>(key("rows"), []);
  const [total, setTotal] = useAdminViewState(key("total"), 0);
  const [offset, setOffset] = useAdminViewState(key("offset"), 0);
  const [filter, setFilter] = useAdminViewState(key("filter"), "");
  const [trashed, setTrashed] = useAdminViewState<"no" | "yes" | "all">(key("trashed"), "no");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useAdminViewState<string | null>(key("openId"), null);

  const { sort, sorted, toggle } = useTableSort<AdminBoardRow, SortKey>(
    rows,
    pickBoardValue,
    { key: "updated", dir: "desc" },
    key("sort")
  );

  const load = useCallback(
    async (opts: { offset: number; q: string; trashed: string; kind: string; append: boolean }) => {
      setLoading(true);
      setErr("");
      try {
        const params = new URLSearchParams();
        params.set("limit", String(PAGE_SIZE));
        params.set("offset", String(opts.offset));
        params.set("trashed", opts.trashed);
        params.set("kind", opts.kind);
        if (opts.q.trim()) params.set("q", opts.q.trim());
        const data = await apiFetch<{
          total: number;
          boards: AdminBoardRow[];
        }>(`/api/v1/admin/boards?${params.toString()}`);
        setTotal(data.total ?? 0);
        setOffset(opts.offset);
        setRows((prev) => (opts.append ? [...prev, ...(data.boards ?? [])] : data.boards ?? []));
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Не удалось загрузить доски");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    // kind за время жизни вкладки не меняется, так что флаг сбрасывать не нужно
    // (и нельзя: StrictMode запускает эффект дважды).
    if (restoredRef.current) return;
    void load({ offset: 0, q: "", trashed: "no", kind, append: false });
  }, [load, kind]);

  function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    void load({ offset: 0, q: filter, trashed, kind, append: false });
  }

  return (
    <div className="wb-admin-users">
      <div className="wb-admin-users__head">
        <h2 className="wb-admin-users__title">
          {kind === "school" ? "Доски школьных аккаунтов" : "Доски внешних аккаунтов"}{" "}
          <span className="wb-admin-users__count">
            {rows.length} из {formatNumber(total)}
          </span>
        </h2>
        <form className="wb-cabinet-panel-create__form" onSubmit={onSubmit}>
          <input
            className="wb-cabinet-panel-create__input"
            type="search"
            placeholder="Название, ID или email владельца…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <select
            className="wb-cabinet-panel-create__input"
            value={trashed}
            onChange={(e) => {
              const next = e.target.value as "no" | "yes" | "all";
              setTrashed(next);
              void load({ offset: 0, q: filter, trashed: next, kind, append: false });
            }}
            aria-label="Корзина"
          >
            <option value="no">Активные</option>
            <option value="yes">В корзине</option>
            <option value="all">Все</option>
          </select>
          <button
            className="wb-board-card__action-btn wb-board-card__action-btn--ghost"
            type="submit"
            disabled={loading}
          >
            Найти
          </button>
        </form>
      </div>

      {err ? (
        <p className="wb-cabinet-toast wb-cabinet-toast--err" role="alert">
          {err}
        </p>
      ) : null}

      <div className="wb-admin-table-wrap">
        <table className="wb-admin-table">
          <thead>
            <tr>
              <SortableTh label="Название" sortKey="title" sort={sort} onSort={toggle} />
              <SortableTh label="Владелец" sortKey="owner" sort={sort} onSort={toggle} />
              <SortableTh label="Объектов" sortKey="objects" sort={sort} onSort={toggle} />
              <SortableTh label="Изменена" sortKey="updated" sort={sort} onSort={toggle} />
              <SortableTh label="Создана" sortKey="created" sort={sort} onSort={toggle} />
              <SortableTh label="ID" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((b) => (
              <tr
                key={b.id}
                className={
                  openId === b.id
                    ? "wb-admin-table__row wb-admin-table__row--active"
                    : "wb-admin-table__row"
                }
                onClick={() => setOpenId(b.id)}
              >
                <td>
                  {b.title || "Без названия"}
                  {b.in_trash ? " · корзина" : ""}
                </td>
                <td>
                  {b.is_guest ? (
                    <span className="wb-admin-users__count">гостевая</span>
                  ) : (
                    b.owner_email ?? "—"
                  )}
                </td>
                <td>{formatNumber(b.objects)}</td>
                <td title={formatDate(b.updated_at)}>{formatRelative(b.updated_at)}</td>
                <td title={formatDate(b.created_at)}>{formatRelative(b.created_at)}</td>
                <td>
                  <AdminCopyId id={b.id} />
                </td>
              </tr>
            ))}
            {!sorted.length && !loading ? (
              <tr>
                <td colSpan={6}>Ничего не найдено</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {rows.length < total ? (
        <button
          type="button"
          className="wb-board-card__action-btn wb-board-card__action-btn--ghost"
          disabled={loading}
          onClick={() =>
            void load({ offset: offset + PAGE_SIZE, q: filter, trashed, kind, append: true })
          }
        >
          {loading ? "Загрузка…" : "Показать ещё"}
        </button>
      ) : null}

      {openId ? <AdminBoardCard boardId={openId} /> : null}
    </div>
  );
}
